import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { HolidayApproverSummary } from '@app/employee-hub/employee-leave/leave-approvals';

import { employeeHubRoutes } from '@app/employee-hub/employee-hub-routes';
import { HolidayApproverSummaryStateService } from '@app/employee-hub/employee-leave/leave-approvals';
import { MenuItem, TopMenuItem } from '../../../../component-library/layout/menu-item/menu-interfaces';
import { SidebarMenuConfig } from '../../../../component-library/layout';

@Injectable({ providedIn: 'root' })
export class EmployeeHubNavigationService {
  constructor(
    public activeSecurity: ActiveSecurityContextStateService,
    private holidayApproverSummaryService: HolidayApproverSummaryStateService
  ) {}

  getSidebarMenuConfig$(): Observable<SidebarMenuConfig> {
    return this.holidayApproverSummaryService.entity$.pipe(
      map((holidayApproverSummary) => this.getSidebarMenuConfig(holidayApproverSummary))
    );
  }

  private fetchBottomNavItems = (): MenuItem[] => {
    const bottomNavItems: MenuItem[] = [];

    bottomNavItems.push({
      label: 'Dashboard',
      route: 'employee-hub/dashboard',
      icon: 'house',
      feature: 'EmployeeHubDashboard',
      type: 'MenuLink'
    } as any);

    if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubDocuments')) {
      bottomNavItems.push({
        label: 'Payslips',
        route: 'employee-hub/payslips',
        icon: 'money',
        feature: 'Payslips',
        type: 'MenuLink'
      } as any);
    }

    return bottomNavItems;
  };

  private getSidebarMenuConfig = (approverSummary: HolidayApproverSummary): SidebarMenuConfig => {
    const sections: TopMenuItem[] = [];
    const mainItems: MenuItem[] = [];

    mainItems.push({
      label: 'Dashboard',
      route: 'employee-hub/dashboard',
      icon: 'house',
      type: 'MenuLink',
      feature: 'EmployeeHubDashboard'
    } as any);

    if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubHolidays'))
      mainItems.push({
        label: 'Calendar',
        route: 'employee-hub/my-calendar',
        icon: 'calendar-dots',
        type: 'MenuLink',
        feature: 'Holidays'
      } as any);

    if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubDocuments'))
      mainItems.push({
        label: 'Payslips',
        route: 'employee-hub/payslips',
        icon: 'money',
        type: 'MenuLink',
        feature: 'Payslips'
      } as any);

    if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubDocuments'))
      mainItems.push({
        label: 'Documents',
        route: 'employee-hub/documents',
        icon: 'files',
        type: 'MenuLink',
        feature: 'HubDocuments'
      } as any);

    if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubPersonalDetails'))
      mainItems.push({
        label: 'Profile',
        route: 'employee-hub/my-profile',
        icon: 'user',
        type: 'MenuLink',
        feature: 'MyProfile'
      } as any);

    if (approverSummary.isHolidayApprover && this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubHolidays')) {
      const approvalSectionLinks = [
        {
          label: 'Requests',
          route: employeeHubRoutes.holidayApprovalsRequests,
          counter: approverSummary.openHolidayRequestCount + (approverSummary.openAbsenceRequestCount ?? 0),
          feature: 'HolidayApprovals',
          section: 'HolidayRequests'
        }
      ];

      approvalSectionLinks.push({
        label: 'Calendar',
        route: employeeHubRoutes.holidayApprovalsCalendar,
        counter: undefined,
        feature: 'HolidayApprovals',
        section: 'ApprovalCalendar'
      });

      approvalSectionLinks.push({
        label: 'Entitlements',
        route: employeeHubRoutes.holidayApprovalsEntitlements,
        counter: undefined,
        feature: 'HolidayApprovals',
        section: 'ApprovalEntitlements'
      });

      approvalSectionLinks.push({
        label: 'History',
        route: employeeHubRoutes.holidayApprovalsHistory,
        counter: undefined,
        feature: 'HolidayApprovals',
        section: 'ApprovalHistory'
      });

      const approvalItems: MenuItem[] = approvalSectionLinks.map(
        (link) =>
          ({
            label: link.label,
            route: link.route,
            counter: link.counter,
            type: 'MenuLink',
            feature: link.feature,
            section: link.section
          }) as any
      );

      sections.push({
        title: 'Approvals',
        icon: 'check-circle',
        counter: approverSummary.openHolidayRequestCount + (approverSummary.openAbsenceRequestCount ?? 0),
        collapsible: true,
        items: approvalItems
      });
    }

    if (mainItems.length > 0) {
      sections.unshift({
        items: mainItems
      });
    }

    return {
      sections,
      bottomNavItems: this.fetchBottomNavItems()
    };
  };
}
